/**
 * Pointing at the body, from the desk mouse.
 *
 * Hands and controllers reach parts through the interaction runtime
 * (`registerInteractions` in `index.ts`), which asks `AtlasHitTester`
 * (`systems/hit-tester.ts`) what a ray or a fingertip touches and turns the
 * answer into `hoverEnter`/`pressStart`/`pressEnd` events. The frameworks
 * have no 2D pointer source, so the desk mouse still needs its own driver —
 * this file — but it ends at the exact same `AtlasHitTester.hitRay()` and is
 * wired to the exact same hover/select handlers in `index.ts`, so there is
 * one hit path and one set of handlers for every input this app has.
 *
 * `hitRay` itself answers with a body id (`registerBodyInteractables` in
 * `index.ts` registers one interactable per body, not per part — see its
 * comment for the measured reason), so this reads the fine part back off
 * `hitTester.lastHit()` after every call, the same seam `onInteractionEvent`
 * uses for hands and controllers.
 *
 * Cost: `hitRay` is a raycast against every visible instance of every system,
 * and the atlas has 2,229 of them. `StableBatchedMesh.raycast` rejects a
 * whole system with one sphere test when the ray cannot reach it, and this
 * still throttles its own hover to 20 Hz rather than resolve on every frame —
 * a hover that updates at 20 Hz is indistinguishable from one that updates at
 * 90, and the difference is 70 raycasts a second not taken.
 */
import { Raycaster, Vector2 } from "three";
import type { World } from "@iwsdk/core";
import type { RayTuple } from "@realitycollective/iwsdk-interactions";
import type { AtlasHitTester } from "./hit-tester.js";

/** Milliseconds between hover updates. */
const HOVER_INTERVAL_MS = 50;

export class Picker {
  private readonly raycaster = new Raycaster();
  private readonly pointer = new Vector2();
  private lastAt = 0;
  private pointerOnScreen = false;

  constructor(
    private readonly world: World,
    private readonly hitTester: AtlasHitTester,
    /** The interactable id (`${bodyId}/${partId}`) under the pointer changed, or `null`. */
    private readonly onHover: (interactableId: string | null) => void,
  ) {}

  /** Track the mouse for the desk build. Coordinates are normalised device space. */
  setPointer(x: number, y: number): void {
    this.pointer.set(x, y);
    this.pointerOnScreen = true;
  }

  clearPointer(): void {
    this.pointerOnScreen = false;
    this.onHover(null);
  }

  /** Per frame, throttled: work out what is under the pointer and report it. */
  update(now: number): void {
    if (now - this.lastAt < HOVER_INTERVAL_MS) return;
    this.lastAt = now;
    this.pick();
  }

  /**
   * Resolve the hover now, ignoring the throttle and without disturbing it.
   *
   * Callers that need an answer in the same tick used to pass `Infinity` to
   * `update`, which parked `lastAt` at infinity and left every later frame
   * taking the throttle's early return forever. This is the fix: it never
   * touches `lastAt`, so the throttle keeps running on its own schedule.
   */
  pickNow(): void {
    this.pick();
  }

  /** Diagnostics for the smoke test when a pick finds nothing. */
  debug(): Record<string, unknown> {
    const ray = this.currentRay();
    const hit = ray ? this.hitTester.hitRay(ray) : null;
    return {
      onScreen: this.pointerOnScreen,
      ray: ray ? { origin: ray.origin.map((n) => Number(n.toFixed(2))), direction: ray.direction.map((n) => Number(n.toFixed(2))) } : null,
      hit,
      lastHit: this.hitTester.lastHit(),
    };
  }

  private currentRay(): RayTuple | null {
    if (!this.pointerOnScreen) return null;
    this.raycaster.setFromCamera(this.pointer, this.world.camera);
    const { origin, direction } = this.raycaster.ray;
    return { origin: [origin.x, origin.y, origin.z], direction: [direction.x, direction.y, direction.z] };
  }

  private pick(): void {
    const ray = this.currentRay();
    if (!ray) {
      this.onHover(null);
      return;
    }
    this.hitTester.hitRay(ray); // resolves the fine hit into `lastHit()`; the body id it returns is not what a mouse hover needs
    this.onHover(this.hitTester.lastHit()?.interactableId ?? null);
  }
}
